import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Route as RouteIcon, CalendarCheck, Stethoscope, Siren, Loader2 } from 'lucide-react';
import ProgrammeStopLine from './ProgrammeStopLine';
import useProgrammeAccess from './useProgrammeAccess';
import { fetchBookingLine } from '../../redux/programmeSlice';
import {
    windowGate, nextStopOf, stopLabel, rangeLabel, collapse, bookingProgrammeBody, EMERGENCY_REASON_MAX,
} from '../../utils/careProgrammes';
import { formatDay } from '../health/healthFormat';
import { tidyName } from '../../utils/tidyName';

// THE PROGRAMME QUESTION AT BOOKING — desk and doctor (doc/programme-visits.md
// §2). Once the patient and the doctor are chosen, the server is asked for
// the line (GET /patient-programmes/booking-line). No active programme of this
// patient with this doctor → nothing is drawn and the booking is unchanged.
// Otherwise: the Programme card (THE STOP LINE, the same look as the visit)
// and ONE question — "What is this appointment for?":
//
//   Programme visit (Stop n)  only on a date inside that stop's window
//                             (`nextWindow`); outside it the choice is shut
//                             with "Opens on <date>" / "Window closed on <date>"
//   A different problem       an ordinary appointment, never linked
//   Emergency (programme)     any date, a reason is required; it takes the
//                             next stop, early
//
// The server picks the stop and re-checks everything (PROGRAMME_* refusals);
// this only keeps the desk from asking for what it would refuse.
//
// The answer (`value`) is { asked, line, purpose, emergencyReason }; the host
// turns it into the booking body with `programmeChoiceBody` and checks it with
// `programmeChoiceProblem` before sending.

export const PURPOSES = ['programme', 'other', 'emergency'];

/** What is wrong with the answer for a booking on `date` — an i18n key, or ''. */
export const programmeChoiceProblem = (value, date) => {
    if (!value?.asked) return '';
    if (!PURPOSES.includes(value.purpose)) return 'programmes.booking.err_pick';
    if (value.purpose === 'programme' && windowGate(date, value.line?.nextWindow) !== 'ok') return 'programmes.booking.err_window';
    if ((value.purpose === 'programme' || value.purpose === 'emergency') && !value.line?.nextStopKey) return 'programmes.booking.err_no_stop';
    if (value.purpose === 'emergency' && !collapse(value.emergencyReason)) return 'programmes.booking.err_reason';
    return '';
};

/** The booking body's `programme`, or null (not asked, or a different problem). */
export const programmeChoiceBody = (value) => (value?.asked
    ? bookingProgrammeBody({
        patientProgrammeId: value.line?.patientProgrammeId,
        purpose: value.purpose,
        emergencyReason: value.emergencyReason,
    })
    : null);

/**
 * @param patientId / doctorId  who the appointment is for and with
 * @param date       the appointment's day, 'YYYY-MM-DD' (the slot's)
 * @param value      the answer (see above), or null
 * @param onChange   (value) => void
 * @param refusal    the server's refusal sentence for the last booking, if it was a PROGRAMME_* one
 * @param showErrors draw the answer's problem (after a submit)
 */
function ProgrammeBookingQuestion({
    patientId, doctorId, date, value, onChange, refusal = '', showErrors = false, className = '',
}) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const access = useProgrammeAccess();
    const [lines, setLines] = useState([]);
    const [loading, setLoading] = useState(false);

    const enabled = Boolean(access.featureOn && patientId && doctorId);

    // Asked once per (patient, doctor); any refusal or failure = ask nothing
    // (the server still judges the booking itself).
    useEffect(() => {
        let alive = true;
        setLines([]);
        onChange?.(null);
        if (!enabled) return undefined;
        setLoading(true);
        dispatch(fetchBookingLine({ patientId, doctorId }))
            .then((res) => {
                if (!alive) return;
                const list = fetchBookingLine.fulfilled.match(res) ? res.payload.programmes : [];
                setLines(list);
                if (list.length) onChange?.({ asked: true, line: list[0], purpose: null, emergencyReason: '' });
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, patientId, doctorId]);

    const line = value?.line || lines[0] || null;
    const next = useMemo(() => nextStopOf(line), [line]);

    if (!enabled) return null;
    if (loading && !lines.length) {
        return (
            <p data-testid="booking-programme-loading" className={`flex items-center gap-2 text-xs text-[#4ABDBD] ${className}`}>
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                {t('programmes.booking.checking')}
            </p>
        );
    }
    if (!lines.length || !line) return null;

    const gate = windowGate(date, line.nextWindow);
    const stopName = next ? stopLabel(next, t) : '';
    const set = (patch) => onChange?.({ asked: true, line, purpose: value?.purpose || null, emergencyReason: value?.emergencyReason || '', ...patch });
    const problem = showErrors ? programmeChoiceProblem(value, date) : '';
    const windowText = line.nextWindow ? rangeLabel(line.nextWindow.from, line.nextWindow.to) : '';

    let programmeWhy = '';
    if (!line.nextStopKey) programmeWhy = t('programmes.booking.no_stop_left');
    else if (gate === 'before') programmeWhy = t('programmes.booking.opens_on', { date: formatDay(line.nextWindow.from) });
    else if (gate === 'after') programmeWhy = t('programmes.booking.closed_on', { date: formatDay(line.nextWindow.to) });
    else if (gate === 'none') programmeWhy = t('programmes.booking.no_window');
    const programmeOk = Boolean(line.nextStopKey) && gate === 'ok';
    const emergencyOk = Boolean(line.nextStopKey);

    const option = (purpose, Icon, title, hint, ok, why) => {
        const checked = value?.purpose === purpose;
        return (
            <label
                key={purpose}
                data-testid={`booking-programme-option-${purpose}`}
                aria-disabled={!ok}
                className={`flex items-start gap-2.5 rounded-xl border px-3 py-2 transition-colors ${checked ? 'border-[#4ABDBD] bg-[#4ABDBD]/10' : 'border-gray-200 bg-white'} ${ok ? 'cursor-pointer hover:border-[#4ABDBD]' : 'cursor-not-allowed opacity-60'}`}
            >
                <input
                    type="radio"
                    name="booking-programme-purpose"
                    value={purpose}
                    checked={checked}
                    disabled={!ok}
                    onChange={() => set({ purpose })}
                    data-testid={`booking-programme-radio-${purpose}`}
                    className="mt-1 accent-[#2D8A8A]"
                />
                <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
                        <Icon className="h-4 w-4 text-[#2D8A8A]" aria-hidden="true" />
                        {title}
                    </span>
                    {hint && <span className="mt-0.5 block text-[11px] text-gray-500">{hint}</span>}
                    {!ok && why && (
                        <span data-testid={`booking-programme-why-${purpose}`} className="mt-0.5 block text-[11px] font-semibold text-[#9a6a2c]">{why}</span>
                    )}
                </span>
            </label>
        );
    };

    return (
        <section data-testid="booking-programme" className={`rounded-2xl border border-[#4ABDBD]/25 bg-gradient-to-br from-[#4ABDBD]/5 to-white p-4 ${className}`}>
            <h4 className="flex items-center gap-2 text-sm font-bold text-[#2D8A8A]">
                <RouteIcon className="h-4 w-4" aria-hidden="true" />
                {t('programmes.card_title')}
            </h4>

            {lines.length > 1 && (
                <div data-testid="booking-programme-pick" className="mt-2 flex flex-wrap gap-1.5" role="radiogroup" aria-label={t('programmes.booking.which_programme')}>
                    {lines.map((l) => {
                        const on = l.patientProgrammeId === line.patientProgrammeId;
                        return (
                            <button
                                key={l.patientProgrammeId}
                                type="button"
                                role="radio"
                                aria-checked={on}
                                data-testid={`booking-programme-pick-${l.patientProgrammeId}`}
                                onClick={() => onChange?.({ asked: true, line: l, purpose: null, emergencyReason: '' })}
                                className={`rounded-full px-3 py-1 text-xs font-semibold ${on ? 'bg-[#2D8A8A] text-white' : 'border border-gray-200 bg-white text-gray-600 hover:border-[#4ABDBD]'}`}
                            >
                                {l.name}
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="mt-2">
                <p data-testid="booking-programme-name" className="text-sm font-semibold text-gray-800">
                    {line.name}
                    {line.doctorName ? <span className="font-normal text-gray-500"> · {t('programmes.by_doctor', { name: tidyName(line.doctorName) })}</span> : null}
                </p>
                {windowText && line.nextStopKey && (
                    <p data-testid="booking-programme-window" className="mt-0.5 text-[11px] text-gray-500">
                        {t('programmes.booking.window_line', { stop: stopName, range: windowText })}
                    </p>
                )}
                <ProgrammeStopLine
                    programme={line}
                    testIdPrefix="booking-programme-stop"
                    highlightKey={line.nextStopKey}
                    showNotes={false}
                    className="mt-2"
                />
            </div>

            <fieldset className="mt-3">
                <legend className="mb-2 text-sm font-bold text-gray-800">{t('programmes.booking.question')}</legend>
                <div className="grid grid-cols-1 gap-2">
                    {option('programme', CalendarCheck,
                        stopName ? t('programmes.booking.purpose_programme', { stop: stopName }) : t('programmes.booking.purpose_programme_plain'),
                        windowText && line.nextStopKey ? t('programmes.booking.purpose_programme_hint', { range: windowText }) : '',
                        programmeOk, programmeWhy)}
                    {option('other', Stethoscope, t('programmes.booking.purpose_other'), t('programmes.booking.purpose_other_hint'), true, '')}
                    {option('emergency', Siren, t('programmes.booking.purpose_emergency'),
                        stopName ? t('programmes.booking.purpose_emergency_hint', { stop: stopName }) : '',
                        emergencyOk, t('programmes.booking.no_stop_left'))}
                </div>
                {value?.purpose === 'emergency' && (
                    <label className="mt-2 block">
                        <span className="mb-1 block text-xs font-semibold text-gray-700">
                            {t('programmes.booking.reason_label')} <span className="text-red-500">*</span>
                        </span>
                        <textarea
                            data-testid="booking-programme-reason"
                            value={value.emergencyReason || ''}
                            maxLength={EMERGENCY_REASON_MAX}
                            rows={2}
                            onChange={(e) => set({ emergencyReason: e.target.value.replace(/[<>]/g, '') })}
                            placeholder={t('programmes.booking.reason_placeholder')}
                            className={`w-full rounded-xl border p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40 ${problem === 'programmes.booking.err_reason' ? 'border-red-300 bg-red-50/40' : 'border-gray-300'}`}
                        />
                    </label>
                )}
                {problem && (
                    <p role="alert" data-testid="booking-programme-error" className="mt-2 text-xs font-medium text-red-600">{t(problem)}</p>
                )}
                {refusal && (
                    <p role="alert" data-testid="booking-programme-refusal" className="mt-2 rounded-xl border border-red-200 bg-red-50 p-2.5 text-xs font-medium text-red-700">{refusal}</p>
                )}
            </fieldset>
        </section>
    );
}

export default ProgrammeBookingQuestion;
